import { makeAutoObservable, runInAction } from 'mobx';
import appointmentService from '../services/appointmentService';

class PartnerAppointmentStore {
  appointments = [];
  currentAppointment = null;
  loading = false;
  error = null;

  // Filters
  serviceFilter = null;
  statusFilter = null;
  dateRangeFilter = null; // { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' }
  searchQuery = '';

  constructor(rootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  // Computed - Filtered appointments
  get filteredAppointments() {
    // Ensure appointments is always an array
    if (!Array.isArray(this.appointments)) {
      return [];
    }
    let filtered = this.appointments;

    if (this.serviceFilter) {
      filtered = filtered.filter(apt => apt.service === this.serviceFilter);
    }

    if (this.statusFilter) {
      filtered = filtered.filter(apt => apt.status === this.statusFilter);
    }

    if (this.dateRangeFilter) {
      filtered = filtered.filter(apt => {
        const aptDate = new Date(apt.appointment_date);
        const start = new Date(this.dateRangeFilter.start);
        const end = new Date(this.dateRangeFilter.end);
        return aptDate >= start && aptDate <= end;
      });
    }

    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(apt =>
        apt.customer_first_name?.toLowerCase().includes(query) ||
        apt.customer_last_name?.toLowerCase().includes(query) ||
        apt.customer_email?.toLowerCase().includes(query) ||
        apt.service_name?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }

  // Computed - Group by status
  get pendingAppointments() {
    return this.appointments.filter(apt => apt.status === 'PENDING');
  }

  get confirmedAppointments() {
    return this.appointments.filter(apt => apt.status === 'CONFIRMED');
  }

  get completedAppointments() {
    return this.appointments.filter(apt => apt.status === 'COMPLETED');
  }

  get cancelledAppointments() {
    return this.appointments.filter(apt => apt.status === 'CANCELLED');
  }

  // Actions - Filters
  setServiceFilter(serviceId) {
    this.serviceFilter = serviceId;
  }

  setStatusFilter(status) {
    this.statusFilter = status;
  }

  setDateRangeFilter(dateRange) {
    this.dateRangeFilter = dateRange;
  }

  setSearchQuery(query) {
    this.searchQuery = query;
  }

  clearFilters() {
    this.serviceFilter = null;
    this.statusFilter = null;
    this.dateRangeFilter = null;
    this.searchQuery = '';
  }

  // Actions - Fetch appointments
  async fetchAppointments(params = {}) {
    this.loading = true;
    this.error = null;

    // Build query params from filters
    const queryParams = { ...params };
    if (this.serviceFilter) queryParams.service = this.serviceFilter;
    if (this.statusFilter) queryParams.status = this.statusFilter;

    const result = await appointmentService.fetchPartnerAppointments(queryParams);

    runInAction(() => {
      if (result.success) {
        // Handle paginated response (results array) or direct array
        this.appointments = result.data.results || result.data || [];
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  async fetchAppointmentById(appointmentId) {
    this.loading = true;
    this.error = null;

    const result = await appointmentService.fetchPartnerAppointmentDetail(appointmentId);

    runInAction(() => {
      if (result.success) {
        this.currentAppointment = result.data;
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  // Actions - Update appointment
  async updateAppointmentStatus(appointmentId, status, partnerNotes = '') {
    this.loading = true;
    this.error = null;

    const updateData = { status };
    if (partnerNotes) {
      updateData.partner_notes = partnerNotes;
    }

    const result = await appointmentService.updateAppointmentStatus(appointmentId, updateData);

    runInAction(() => {
      if (result.success) {
        // Update in list
        const index = this.appointments.findIndex(apt => apt.id === appointmentId);
        if (index !== -1) {
          this.appointments[index] = result.data;
        }
        // Update current appointment if viewing
        if (this.currentAppointment?.id === appointmentId) {
          this.currentAppointment = result.data;
        }
      } else {
        this.error = result.error;
      }
      this.loading = false;
    });

    return result;
  }

  // Reset
  reset() {
    this.appointments = [];
    this.currentAppointment = null;
    this.loading = false;
    this.error = null;
    this.clearFilters();
  }
}

export default PartnerAppointmentStore;
